// Per-row doc-copy on the lead list. Replaces the expandable
// LeadListDocsDisclosure: clicking copies the party's most-recent
// doc number straight to the clipboard — one click, no expansion,
// no extra read.
//
// Rationale: the lead-list row's job is rapid skip-trace data entry
// (click ▸ docs to expand → mouse-pick a number → copy → paste into
// Weld clerk → read lease → type city/state). Folding "expand" into
// the same affordance as "copy" cuts a click. The bulk "Copy N docs"
// action at the bottom of the page still pipe-delimits multi-row
// copies, and PartyDetail still lists all docs for the rare case
// the operator wants a non-latest one.
//
// tabIndex={-1} keeps this OUT of the keyboard typing flow on the
// row — same defense the disclosure had.

import { useState } from 'react';

interface Props {
  docNumber: string | null;
}

export function LeadListDocCopy({ docNumber }: Props) {
  const [copied, setCopied] = useState(false);

  async function doCopy(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!docNumber) return;
    try {
      await navigator.clipboard.writeText(docNumber);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = docNumber; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  if (!docNumber) {
    return (
      <span
        title="No recorder doc on file for this party"
        className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono rounded border border-border text-text-ghost"
      >no doc</span>
    );
  }

  return (
    <button
      type="button"
      onClick={doCopy}
      tabIndex={-1}
      title={`Copy ${docNumber} (most-recent doc) for the Weld recorder`}
      className={
        'inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-mono rounded border transition-colors ' +
        (copied
          ? 'border-green/40 bg-green/10 text-green'
          : 'border-border text-text-faint hover:text-text hover:border-border-bright')
      }
    >
      {copied ? `✓ ${docNumber}` : `${docNumber} ↗`}
    </button>
  );
}
